import { Injectable } from '@angular/core';
import { ApiService } from "../api/api.service";
import { BehaviorSubject, forkJoin, Observable, Subject, of } from "rxjs";
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { map, shareReplay, tap } from "rxjs/operators";
import * as Cookies from "js-cookie";
import { ConstantsService as constants } from "../constants/constants.service";
import { RegionService } from "../region/region.service";
import { environment } from 'src/environments/environment';
import { PermissionsService } from '../permissions/permissions.service';

@Injectable({
  providedIn: 'root'
})
export class OrganisationService {

  availableOrgs: any = [];
  organisationDetails!: Observable<any>;
  departmentList!: Observable<any>;
  storedOrgUid = '';
  details: any;
  organisationBrandName: any;
  private newDepartments = new BehaviorSubject<any>(null);
  private departments = new BehaviorSubject<any>(null);
  private switchOrg = new BehaviorSubject<any>(false);
  dropdownOpen = false;
  surveyTypeMapping = {
    1: 'exit',
    2: 'pulse',
    3: 'reference',
  };

  dropdownOpenChange: Subject<boolean> = new Subject<boolean>();

  constructor(
    private api: ApiService,
    private http: HttpClient,
    private region: RegionService,
    private permissions: PermissionsService
  ) {
    this.dropdownOpenChange.subscribe((value) => {
      this.dropdownOpen = value
    });
  }

  getSwitchOrg() {
    return this.switchOrg.asObservable()
  }

  setSwitchOrg(value) {
    this.switchOrg.next(value)
  }

  createTeam(teamData: any): Observable<any> {
    const endpoint = 'organisation/team';
    return this.api.post(endpoint, teamData).pipe(map(res => res['data']));
  }

  updateTeam(teamData: any): Observable<any> {
    const endpoint = 'organisation/team';
    return this.api.put(endpoint, teamData).pipe(map(res => res));
  }

  listTeam(queryData?: string, rowsPerView = 0, pageNumber = 0, sortString?: string): Observable<any> {
    const endpoint = 'organisation/team/list';
    let params = new HttpParams();

    if (rowsPerView != 0) {
      params = params.set('limit', String(rowsPerView))
        .set('offset', String(rowsPerView * (pageNumber - 1)));
    }
    if (queryData) {
      params = params.set('query', queryData);
    }

    if (sortString) {
      params = params.set('sort_string', sortString);
    }
    // if (roles && roles.length != 0) {
    //   params = params.set('roles', roles.join(','));
    // }
    return this.api.get(endpoint, { params }).pipe(map(data => data['data']));
  }

  saveSurveyPermissions(userUUId: string, inviteeID: number, questionnaires: any): Observable<any> {
    let endpoint = "";
    if (userUUId){
      endpoint = `organisation/user/${userUUId}/surveys`;
    }
    else if(inviteeID){
      endpoint = `organisation/invite/${inviteeID}/surveys`;
    }

    return this.api.post(endpoint, {questionnaires: questionnaires}).pipe(map(data => data['data']));
  }

  listSurveyPermissions(userUUId: string, inviteeID: number, queryType?: string, querySearch?: string, rowsPerView = 0, pageNumber = 0, sortString?: string, filters?: string, surveyTypes?: any, cloned?: boolean): Observable<any> {
    let endpoint = "";
    if (userUUId) {
      endpoint = `organisation/user/${userUUId}/surveys`;
    } else if (inviteeID) {
      endpoint = `organisation/invite/${inviteeID}/surveys`;
    } else {
      endpoint = "questionnaire/organisation/survey/all";
    }
    let params = new HttpParams();

    if (rowsPerView != 0) {
      params = params.set('limit', String(rowsPerView))
        .set('offset', String(rowsPerView * (pageNumber - 1)));
    }
    if (querySearch && queryType) {
      params = params.set(queryType, querySearch);
    }

    if(surveyTypes && surveyTypes.length > 0) {
      params = params.set('survey_types', surveyTypes.join(','));
    }

    if(cloned){
      params = params.set('cloned', 1);
    }

    if (sortString) {
      params = params.set('sort', sortString);
    }
    if (filters) {
      params = params.set('filter', filters)
    }
    return this.api.get(endpoint, {params}).pipe(map(data => {
      if (userUUId || inviteeID) {
        return data['data'];
      } else {
        let questionnaireData = {};
        questionnaireData["total"] = data.data.total
        questionnaireData['data'] = data.data.questionnaires.map(survey => {
          survey.survey_type = this.surveyTypeMapping[survey.survey_type];
          return survey;
        });
        return questionnaireData;
      }
    }));
  }
  removeTeamValidate(teamData: any): Observable<any> {
    let params = new HttpParams();
    const endpoint = 'organisation/remove-team-validate';
    params = params.set('id', teamData.id);
    params = params.set('name', teamData.name);
    return this.api.get(endpoint, { params }).pipe(map(data => data['data']));
  }


  deleteTeam(removeTeamData: any): Observable<any> {
    // let params = new HttpParams();
    // params = params.set('id', removeTeamData.id);
    // params = params.set('reassign_type', removeTeamData.reassignType);
    // params = params.set('reassign_team', removeTeamData.reassignTeam);

    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      body: {id: removeTeamData.id,
             reassign_type: removeTeamData.reassignType,
             do_not_reassign: removeTeamData.doNotReassign,
             reassign_team: removeTeamData.reassignTeam},
    };

    const endpoint = 'organisation/team';
    return this.api.delete(endpoint, options).pipe(
      map(data => data ? data['data'] : null)
    );
  }

  teamUsers(teamId = 0, searchOptions?: any, rowsPerView = 0, pageNumber = 0, sortString?: string, sortOrder = ''): Observable<any> {
    const endpoint = 'organisation/team/users';

    let params = new HttpParams();
    params = params.set('team_id', teamId);

    if (searchOptions) {
      searchOptions.forEach(element => {
        params = params.append(element.key, element.value);
      });
    }

    if (rowsPerView != 0) {
      params = params.set('limit', String(rowsPerView))
        .set('offset', String(rowsPerView * (pageNumber - 1)));
    }
    if (sortString) {
      params = params.set('sort', sortString);
      params = params.set('sortOrder', sortOrder);
    }

    return this.api.get(endpoint, { params }).pipe(map(data => data['data']));
  }

  modifyTeamUsers(teamUsersData: any): Observable<any> {
    const endpoint = 'organisation/team/modify-team-users';
    return this.api.post(endpoint, teamUsersData).pipe(map(res => res['data']));
  }

  updateOrganisationNotes(organisationData: any): Observable<any> {
    const endpoint = 'organisation/notes';
    return this.api.put(endpoint, organisationData).pipe(map(res => res))
  }

  listOrganisation(): Observable<any> {
    const endpoint = 'organisation/list';
    return this.api.get(endpoint).pipe(map(data => data['data']));
  }

  listOrganisationForAllRegions(): Observable<any> {
    const endpoint = 'organisation/list/all';
    return this.api.get(endpoint).pipe(map(data => data['data']));
  }

  getOrganisation(): Observable<any> {
    if(!this.organisationDetails) {
      // Need to retrieve organisation data
      this.organisationDetails = this.api.get('organisation').pipe(map(data => data['data']), tap(data => this.storedOrgUid = data.uid), shareReplay(1));
    }

    return this.organisationDetails;
  }

  setOrganisationDetails(data) {
    this.details = data
  }

  getOrganisationDetails() {
    return this.details
  }

  updateOrganisation(organisationData: any): Observable<any> {
    const endpoint = 'organisation';
    return this.api.put(endpoint, organisationData).pipe(map(res => res['data']));
  }

  getOrganisationRequiredProfileCredit(checkType, draftUid): Observable<any> {
    let params = new HttpParams();
    params = params.set("check_type", checkType);
    params = params.set("draft_uid", draftUid);

    const endpoint = 'organisation/profile_credits';
    return this.api.get(endpoint, { params }).pipe(map(res => res['data']));
  }

  deprecatedGetOrganisationRequiredProfileCredit(checkType, draftUid, orgUid): Observable<any> {
    let params = new HttpParams();
    params = params.set("check_type", checkType);
    params = params.set("draft_uid", draftUid);
    params = params.set("org_uid", orgUid);

    const endpoint = 'organisation/profile_credits';
    return this.api.get(endpoint, { params }).pipe(map(res => res['data']));
  }

  getOrganisationUsers(queryData?: string, rowsPerView = 0, pageNumber = 0, sortBy?: string, sortOrder?: string, roles?: any): Observable<any> {
    let endpoint = '';
    let params = new HttpParams();
    if (rowsPerView != 0) {
      params = params.set('limit', String(rowsPerView))
        .set('offset', String(rowsPerView * (pageNumber - 1)));
    }
    if (queryData) {
      params = params.set('query', queryData);
    }
    if (sortBy) {
      params = params.set('sort', sortBy);
    }
    if (sortOrder) {
      params = params.set('sort_order', sortOrder);
    }
    if (roles && roles.length != 0) {
      params = params.set('roles', roles.join(','));
    }
    endpoint = 'organisation/user/list';
    return this.api.get(endpoint, { params }).pipe(map(data => data['data']));
  }

    retrieveOrganisationUsers(queryType?: string, queryData?: string, rowsPerView = 0, pageNumber = 0, sortBy?: string, sortOrder?: string, roles?: any): Observable<any> {
    let endpoint = '';
    let params = new HttpParams();
    if (rowsPerView != 0) {
      params = params.set('limit', String(rowsPerView))
        .set('offset', String(rowsPerView * (pageNumber - 1)));
    }
    if (queryData && queryType) {
      params = params.set(queryType, queryData);
    }
    if (sortBy) {
      params = params.set('sort', sortBy);
    }
    if (sortOrder) {
      params = params.set('sort_order', sortOrder);
    }
    if (roles && roles.length != 0) {
      params = params.set('roles', roles.join(','));
    }
    endpoint = 'organisation/user/list';
    return this.api.get(endpoint, { params }).pipe(map(data => data['data']));
  }

  retrieveOrganisationInvitedUsers(queryType?: string, queryData?: string, rowsPerView = 0, pageNumber = 0, sortBy?: string, sortOrder?: string, roles?: any): Observable<any> {
    let endpoint = '';
    let params = new HttpParams();
    if (rowsPerView != 0) {
      params = params.set('limit', String(rowsPerView))
        .set('offset', String(rowsPerView * (pageNumber - 1)));
    }
    if (queryData && queryType) {
      params = params.set(queryType, queryData);
    }
    if (sortBy) {
      params = params.set('sort', sortBy);
    }
    if (sortOrder) {
      params = params.set('sort_order', sortOrder);
    }
    if (roles && roles.length != 0) {
      params = params.set('roles', roles.join(','));
    }
    endpoint = 'organisation/invite/list';
    return this.api.get(endpoint, { params }).pipe(map(data => data['data']));
  }

  getOrganisationInvitedUsers(queryData?: string, rowsPerView = 0, pageNumber = 0, sortBy?: string, sortOrder?: string, roles?: any): Observable<any> {
    let endpoint = '';
    let params = new HttpParams();
    if (rowsPerView != 0) {
      params = params.set('limit', String(rowsPerView))
        .set('offset', String(rowsPerView * (pageNumber - 1)));
    }
    if (queryData) {
      params = params.set('query', queryData);
    }
    if (sortBy) {
      params = params.set('sort', sortBy);
    }
    if (sortOrder) {
      params = params.set('sort_order', sortOrder);
    }
    if (roles && roles.length != 0) {
      params = params.set('roles', roles.join(','));
    }
    endpoint = 'organisation/invite/list';
    return this.api.get(endpoint, { params }).pipe(map(data => data['data']));
  }

  deprecatedGetOrganisationInvitedUsers(uid: string, queryData?: string, rowsPerView = 0, pageNumber = 0, sortBy?: string, sortOrder?: string, roles?: any): Observable<any> {
    let endpoint = '';
    let params = new HttpParams();
    if (rowsPerView != 0) {
      params = params.set('limit', String(rowsPerView))
        .set('offset', String(rowsPerView * (pageNumber - 1)));
    }
    if (queryData) {
      params = params.set('query', queryData);
    }
    if (sortBy) {
      params = params.set('sort', sortBy);
    }
    if (sortOrder) {
      params = params.set('sort_order', sortOrder);
    }
    if (roles && roles.length != 0) {
      params = params.set('roles', roles.join(','));
    }
    params = params.set("org_uid", uid);
    endpoint = 'organisation/invite/list';
    return this.api.get(endpoint, { params }).pipe(map(data => data['data']));
  }

  deleteOrganisationUser(user: number): Observable<any> {
    let params = new HttpParams().set('user_uid', user["user_uid"]);
    const endpoint = 'organisation/user';
    return this.api.delete(endpoint, { params }).pipe(map(data => data['data']));
  }

  deprecatedDeleteOrganisationUser(uid: string, user: number): Observable<any> {
    let params = new HttpParams().set('org_uid', uid).set('user_uid', user["user_uid"]);
    const endpoint = 'organisation/user';
    return this.api.delete(endpoint, { params }).pipe(map(data => data['data']));
  }

  deleteOrganisationInvitedUser(invitedUser): Observable<any> {
    let params = new HttpParams().set('email', invitedUser["email"]);
    const endpoint = 'organisation/invite/user';
    return this.api.delete(endpoint, { params }).pipe(map(data => data['data']));
  }

  resendInvitation(queryParam): Observable<any> {
    const endpoint = 'organisation/invite/resend';
    let params = new HttpParams().set('email', queryParam);
    return this.api.post(endpoint, undefined, { params }).pipe(map(data => data['data']));
  }

  filterUsers(roles): Observable<any> {
    const endpoint = 'organisation/user/list?roles=' + roles;
    return this.api.get(endpoint).pipe(map(data => data['data']));
  }

  filterInvitedUsers(roles): Observable<any> {
    const endpoint = 'organisation/invite/list?roles=' + roles;
    return this.api.get(endpoint).pipe(map(data => data['data']));
  }

  sortUsers(sort, sort_order): Observable<any> {
    let options = new HttpParams()
    options.set("sort", sort)
    options.set("sort_order", sort_order)
    const endpoint = 'organisation/user/list';
    return this.api.get(endpoint, options).pipe(map(data => data['data']));
  }

  getUserFilterCount(searchType: string, searchQuery: string, filters?: string): Observable<any> {
    let params = new HttpParams().set(searchType, searchQuery);
    if (filters) {
      params = params.set('roles', filters)
    }
    const endpoint = 'organisation/user/count';
    return this.api.get(endpoint, { params }).pipe(map(data => data['data']));
  }

  getUserCount(searchQuery: string): Observable<any> {
    let params = new HttpParams().set('query', searchQuery);
    const endpoint = 'organisation/user/count';
    return this.api.get(endpoint, { params }).pipe(map(data => data['data']));
  }

  getInvitedUserCount(searchQuery: string): Observable<any> {
    let params = new HttpParams().set('query', searchQuery);
    const endpoint = 'organisation/invite/count';
    return this.api.get(endpoint, { params }).pipe(map(data => data['data']));
  }
  retrieveInvitedUserCount(searchType: string, searchQuery: string, filters?: string): Observable<any> {
    let params = new HttpParams().set(searchType, searchQuery);
    if (filters) {
      params = params.set('roles', filters)
    }

    const endpoint = 'organisation/invite/count';
    return this.api.get(endpoint, { params }).pipe(map(data => data['data']));
  }


  deprecatedGetInvitedUserCount(org_uid, searchQuery: string): Observable<any> {
    let params = new HttpParams().set('query', searchQuery).set('org_uid', org_uid);
    const endpoint = 'organisation/invite/count';
    return this.api.get(endpoint, { params }).pipe(map(data => data['data']));
  }

  setOrganisationInvitedUser(data): Observable<any> {
    const endpoint = 'organisation/invite';
    return this.api.post(endpoint, data).pipe(map(data => data['data']));
  }

  deprecatedSetOrganisationInvitedUser(uid: string, data): Observable<any> {
    const endpoint = 'organisation/invite?org_uid=' + uid;
    return this.api.post(endpoint, data).pipe(map(data => data['data']));
  }

  updateOrganisationUserAndInvitedUser(data, isUserUpdate: boolean): Observable<any> {
    let endpoint = ''
    if (isUserUpdate) {
      endpoint = 'organisation/user';
    } else {
      endpoint = 'organisation/invite/user';
    }
    return this.api.put(endpoint, data).pipe(map(data => data['data']));
  }

  getTemplates(defaultTemplate: boolean, templateType: string, resource_type?: string): Observable<any> {
    let params = new HttpParams()
    params = params.set("template_type", templateType);
    if (defaultTemplate) {
      params = params.set("system_default", 'True');
    }
    if (resource_type) {
      params = params.set("resource_type", resource_type);
    }
    const endpoint = 'organisation/email_sms_templates';
    return this.api.get(endpoint, { params });
  }

  deprecatedGetTemplates(orgUid: any, defaultTemplate: boolean, templateType: string, resource_type?: string): Observable<any> {
    let params = new HttpParams()
    params = params.set("template_type", templateType);
    if (defaultTemplate) {
      params = params.set("system_default", 'True');
    }
    if (resource_type) {
      params = params.set("resource_type", resource_type);
    }
    params = params.set("org_uid", orgUid);
    const endpoint = 'organisation/email_sms_templates';
    return this.api.get(endpoint, { params });
  }

  getTemplate(defaultTemplate: boolean, language: string, templateType: string, resource_type?: string): Observable<any> {
    let params = new HttpParams()
    params = params.set("template_type", templateType);
    if (defaultTemplate) {
      params = params.set("language", language);
      params = params.set("system_default", 'True');
    }
    if (resource_type) {
      params = params.set("resource_type", resource_type);
    }
    const endpoint = 'organisation/email_sms_templates';
    return this.api.get(endpoint, { params });
  }

  deprecatedGetTemplate(orgUid: any, defaultTemplate: boolean, language: string, templateType: string, resource_type?: string): Observable<any> {
    let params = new HttpParams()
    params = params.set("template_type", templateType);
    if (defaultTemplate) {
      params = params.set("language", language);
      params = params.set("system_default", 'True');
    }
    if (resource_type) {
      params = params.set("resource_type", resource_type);
    }
    params = params.set("org_uid", orgUid);
    const endpoint = 'organisation/email_sms_templates';
    return this.api.get(endpoint, { params });
  }

  getOrganisationByBrandName(brand_name: string): Observable<any> {
    let params = new HttpParams().set('brand_name', brand_name);
    const endpoint = 'organisation';
    return this.api.get(endpoint, { params }).pipe(map(data => data['data']));
  }

  updateOrganisationTemplate(templateType: string, templates: any): Observable<any> {
    let params = new HttpParams().set("template_type", templateType);
    const endpoint = 'organisation/email_sms_templates';
    return this.api.put(endpoint, templates, { params }).pipe(map(data => data['data']))
  }

  deprecatedUpdateOrganisationTemplate(orgUid: any, templateType: string, templates: any): Observable<any> {
    let params = new HttpParams();
    params = params.set("template_type", templateType);
    params = params.set("org_uid", orgUid);
    const endpoint = 'organisation/email_sms_templates';
    return this.api.put(endpoint, templates, { params }).pipe(map(data => data['data']))
  }

  checkEmailSmsPlaceholders(templates: any, templateType: string): Observable<any> {
    let params = new HttpParams().set('template_type', templateType);
    let payload = {
      'email_sms': templates
    }
    const endpoint = 'organisation/email_sms_templates/validate';
    return this.api.post(endpoint, payload, { params }).pipe(map(res => res['data']));
  }

  deprecatedLoginToRecruiter(): Observable<any> {
    const endpoint = 'organisation/authenticate_to_recruiter';
    return this.api.post(endpoint).pipe(map(data => data['data']));
  }

  getXrefApiKey(marketplaceShortName): Observable<any> {
    let params = new HttpParams().set("marketplace_check", marketplaceShortName);
    const endpoint = 'organisation/api_key';
    return this.api.get(endpoint, { params }).pipe(map(data => data['data']))
  }

  getExpiryReportData(): Observable<any> {
    const endpoint = 'organisation/report-expiry';
    return this.api.get(endpoint).pipe(map(data => data['data']))
  }
  getApiKey(shortNameKeys): Observable<any> {
    // shortNameKeys expected to be an object containing field name then the short name
    // e.g. {'marketplace_platform', 'short_name'}
    let params = new HttpParams()
    if(Object.keys(shortNameKeys).length > 0)
    {
      const keys = Object.keys(shortNameKeys);
      keys.forEach((key, index) => {
        params = params.set(key, shortNameKeys[key]);
      });
    }
    const endpoint = 'organisation/api_key';
    return this.api.get(endpoint, { params }).pipe(map(data => data['data']))
  }

  deprecatedGetXrefApiKey(orgUid, marketplaceShortName): Observable<any> {
    let params = new HttpParams();
    params = params.set("org_uid", orgUid);
    params = params.set("marketplace_check", marketplaceShortName);
    const endpoint = 'organisation/api_key';
    return this.api.get(endpoint, { params }).pipe(map(data => data['data']))
  }

  postXrefApiKey(marketplaceShortName, xrefApiKey): Observable<any> {
    let params = new HttpParams().set("marketplace_check", marketplaceShortName);
    const endpoint = 'organisation/api_key';
    return this.api.post(endpoint, { 'value': xrefApiKey }, { params }).pipe(map(data => data['data']))
  }

  postReportExpiryData(tokenExpiry, tokenValue): Observable<any> {
    const endpoint = 'organisation/report-expiry';
    return this.api.put(endpoint, { 'token_expiry': tokenExpiry, token_value: tokenValue }).pipe(map(data => data['data']))
  }

  postApiKey(shortNameKeys, apiKey): Observable<any> {
    // shortNameKeys expected to be an object containing field name then the short name
    // e.g. {'marketplace_platform', 'short_name'}
    let params = new HttpParams()
    if(Object.keys(shortNameKeys).length > 0)
    {
      const keys = Object.keys(shortNameKeys);
      keys.forEach((key, index) => {
        params = params.set(key, shortNameKeys[key]);
      });
    }
    const endpoint = 'organisation/api_key';
    return this.api.post(endpoint, { 'value': apiKey }, { params }).pipe(map(data => data['data']))
  }

  deprecatedPostXrefApiKey(orgUid, marketplaceShortName, xrefApiKey): Observable<any> {
    let params = new HttpParams();
    params = params.set("org_uid", orgUid);
    params = params.set("marketplace_check", marketplaceShortName);
    const endpoint = 'organisation/api_key';
    return this.api.post(endpoint, { 'value': xrefApiKey }, { params }).pipe(map(data => data['data']))
  }

  deleteXrefApiKey(marketplaceShortName): Observable<any> {
    let params = new HttpParams().set("marketplace_check", marketplaceShortName);
    const endpoint = 'organisation/api_key';
    return this.api.delete(endpoint, { params }).pipe(map(data => data['data']))
  }

  deleteApiKey(shortNameKeys): Observable<any> {
    // shortNameKeys expected to be an object containing field name then the short name
    // e.g. {'marketplace_platform', 'short_name'}
    let params = new HttpParams()
    if(Object.keys(shortNameKeys).length > 0)
    {
      const keys = Object.keys(shortNameKeys);
      keys.forEach((key, index) => {
        params = params.set(key, shortNameKeys[key]);
      });
    }
    const endpoint = 'organisation/api_key';
    return this.api.delete(endpoint, { params }).pipe(map(data => data['data']))
  }

  trustMarketplaceStatus(): Observable<any> {
    let params = new HttpParams()
    const endpoint = 'organisation/trust_marketplace_status';
    return this.api.get(endpoint, { params }).pipe(map(data => data['data']))
  }
  orgTrustMarketPlaceStatus(): Observable<any> {
    let params = new HttpParams()
    const endpoint = 'organisation/trust_marketplace_status?org-token=true';
    return this.api.get(endpoint, { params }).pipe(map(data => data['data']))
  }

  setOrganisationBrandName(name) {
    this.organisationBrandName = name
  }

  getOrganisationBrandName() {
    return this.organisationBrandName
  }

  validateDomains(domain: any): Observable<any> {
    const endpoint = 'organisation/validate_domain?domain=' + domain
    return this.api.get(endpoint).pipe(map(res => res))
  }

  getDepartments(): Observable<any> {
    const endpoint = 'organisation/departments'
    return this.api.get(endpoint).pipe(map(res => res))
  }

  updateDepartments(departments: any): Observable<any> {
    const endpoint = 'organisation/departments'
    return this.api.patch(endpoint, departments).pipe(map(res => res))
  }
  getDepartmentCategories(): Observable<any> {
    const endpoint = 'organisation/department_categories'
    return this.api.get(endpoint).pipe(map(res => res))
  }

  getAllDepartments(forceRefresh = false) {
    if(!this.departmentList || forceRefresh) {
      // Need to retrieve department data
      const endpoint = 'organisation/departments';
      this.departmentList = this.api.get(endpoint).pipe(map(data => data['data']), shareReplay(1));
    }

    return this.departmentList;
  }

  getAllDetailedDepartments(forceRefresh = false) {
    if (!this.departmentList || forceRefresh) {
      // Need to retrieve department data
      const endpoint = 'organisation/departments?detail=true';
      this.departmentList = this.api.get(endpoint).pipe(map(data => data['data']), shareReplay(1));
    }
    return this.departmentList ? this.departmentList : of([]);
  }

  getEmailDomains() {
    const endpoint = 'organisation/domains';
    return this.api.get(endpoint).pipe(map(res => res))
  }

  addEmailDomains(domain_list: any) {
    const endpoint = 'organisation/domains';
    return this.api.put(endpoint, { domain: domain_list }).pipe(map(res => res['data']));
  }

  deleteEmailDomains(domain_list: any) {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      body: {domain: domain_list},
    };
    const endpoint = 'organisation/domains';
    return this.api.delete(endpoint, options).pipe(map(res => res['data']));
  }

  addDepartments(departments: any): Observable<any> {
    const endpoint = 'organisation/departments'
    return this.api.post(endpoint, departments).pipe(map(res => res))
  }
  deleteDepartments(departments: any): Observable<any> {
    let params = new HttpParams()
      .set('old_departments', departments.map(each => each.oldDepartment).join(','))
      .set('new_departments', departments.map(each => each.newDepartment).join(','));

    const endpoint = 'organisation/departments'
    return this.api.delete(endpoint, { params }).pipe(map(res => res))
  }

  getDepartmentsDuplicateParams(searchOptions?:any, rowsPerPage=0, pageNumber=1, sortString?: string, filterOptions?:any, callerComponent=''): Observable<any> {
    const endpoint = 'organisation/departments';
    let params = new HttpParams();
    if (rowsPerPage){
      params = params.set('limit', String(rowsPerPage));
      params = params.set('offset', String((pageNumber-1)*rowsPerPage))
    }
    if (searchOptions) {
      searchOptions.forEach(element => {
        params = params.append(element.key, element.value);
      });
    }
    if (sortString) {
      params = params.set('sort', sortString)
    }
    if (callerComponent){
      params = params.set('caller', callerComponent)
    }
    return this.api.get(endpoint, {params}).pipe(map(res => res));
  }
  setNewDepartments(departments: any): void{
    this.newDepartments.next(departments);
  }

  getNewDepartments(): any{
    return this.newDepartments.asObservable()
  }

  getPaginatedDepartments(offset, limit, sort): Observable<any> {
    let endpoint = `organisation/departments?detail=true&limit=${limit}&offset=${offset}&sort=${sort}`;
    return this.api.get(endpoint).pipe(map(res => res))
  }
  getDepartmentCheck(name): Observable<any> {
    let endpoint = `organisation/check/department?department=${name}`;
    return this.api.get(endpoint).pipe(map(res => res))
  }

  SetPrefectehedDepartment(data): void{
    this.departments.next(data)
  }
  GetPrefectehedDepartment(): any{
    return this.departments.asObservable()
  }
  setDropdownOpen(isOpen) {
    this.dropdownOpen = isOpen;
  }

  getTurnOver() {
    return this.api.get('organisation/turnover_plan');
  }

  getTeams(offset, limit, sort, search) {
    return this.api.get(`organisation/user/teams?offset=${offset}&limit=${limit}&sort=${sort}&search=${search}`)
  }

  getUserTeams(user_id, offset, limit, sort, search) {
    return this.api.get(`organisation/user/${user_id}/teams?offset=${offset}&limit=${limit}&sort=${sort}&search=${search}`)
  }

  getInviteeTeam(invitee_id, offset, limit, sort, search) {
    return this.api.get(`organisation/invite/${invitee_id}/teams?offset=${offset}&limit=${limit}&sort=${sort}&search=${search}`)
  }

  updateUserTeam(user_id, payload) {
    return this.api.put(`organisation/user/${user_id}/teams`, payload)
  }

  updateInviteeTeam(invitee_id, payload) {
    return this.api.put(`organisation/invite/${invitee_id}/teams`, payload)
  }

  cloneUser(user_id: string) {
    return this.api.get(`organisation/user/${user_id}/clone`, {})
  }

  cloneInvitee(id: string) {
    return this.api.get(`organisation/invitee/${id}/clone`);
  }

  addUser(payload: any) {
    return this.api.post(`organisation/invite`, payload);
  }

  getCbPlanItems() {
    return this.api.get(`organisation/cb-plan-item-price`);
  }

  addAuditLog(payload: any) {
    return this.api.post(`log_request/`, payload);
  }

  setupStripeBilling(billingData: any): Observable<any> {
    const endpoint = 'stripe/setup-stripe-billing/';
    return this.api.post(endpoint, billingData).pipe(map(res => res));
  }
}
